import { memo } from "react"
import { Box, Typography, IconButton, Tooltip } from "@mui/material"
import { useGridApiContext, useGridSelector, gridRowCountSelector } from "@mui/x-data-grid-premium"
import { Compress, Expand } from "@mui/icons-material"
import { resizeColumnsByHeaderNode, collapseColumns } from "../helpers"
import { IntradayPricingSummaryPropType } from "../prop-types"

function formatRatioAsPercent(ratio) {
  // Reserve 100% / 0% for exact ratios — rounding a 0.996 ratio to "100%" while
  // securities sit on a prior close revives the misleading figure the ticket forbids.
  if (ratio >= 1) {
    return "100%"
  }
  if (ratio <= 0) {
    return "0%"
  }
  return `${Math.min(99, Math.max(1, Math.round(ratio * 100)))}%`
}

function IntradayPricingSummaryLabel({ summary }) {
  const { pricedTodayWeightRatio, pricedTodayCountRatio, holdingCount } = summary

  return (
    <Tooltip title={`${formatRatioAsPercent(pricedTodayCountRatio)} of ${holdingCount} holdings by count`}>
      <Typography variant="caption" className="custom-footer-label" data-test-class="intraday-priced-today">
        {`Priced today: ${formatRatioAsPercent(pricedTodayWeightRatio)} (by weight)`}
      </Typography>
    </Tooltip>
  )
}

IntradayPricingSummaryLabel.propTypes = {
  summary: IntradayPricingSummaryPropType.isRequired,
}

const TableFooter = memo(({ intradayPricingSummary = null }) => {
  const apiRef = useGridApiContext()
  const totalRowCount = useGridSelector(apiRef, gridRowCountSelector)

  return (
    <Box className="custom-footer">
      <Box className="custom-footer-left">
        <Tooltip title="Collapse columns">
          <IconButton
            size="small"
            onClick={() => collapseColumns(apiRef)}
          >
            <Compress
              fontSize="small"
              sx={{ transform: "rotate(90deg)" }}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title="Expand columns">
          <IconButton
            size="small"
            onClick={() => resizeColumnsByHeaderNode(apiRef)}
          >
            <Expand
              fontSize="small"
              sx={{ transform: "rotate(90deg)" }}
            />
          </IconButton>
        </Tooltip>
      </Box>
      <Box className="custom-footer-center">
        {intradayPricingSummary && <IntradayPricingSummaryLabel summary={intradayPricingSummary} />}
      </Box>
      <Box className="custom-footer-right">
        <Typography variant="caption" className="custom-footer-label">
          {totalRowCount} {totalRowCount === 1 ? "row" : "rows"}
        </Typography>
      </Box>
    </Box>
  )
})

TableFooter.displayName = "TableFooter"

TableFooter.propTypes = {
  intradayPricingSummary: IntradayPricingSummaryPropType,
}

export default TableFooter
